import { createContext, useContext, useEffect, useRef, useState } from 'react';

import type { PropsWithChildren } from 'react';

import { eventService, WindowEvents } from 'services/eventService';

export type ScrollableHeaderContextType = {
  scrollThreshold: number;
  addHeaderHeight: (h: number) => void;
  classNameSuffix: string;
  animationClass: string;
  headerRoom: number;
  setHeaderRoom: (h: number) => void;
  headerTop: number;
  setHeaderTop: (h: number) => void;
};

const ScrollableHeaderContext = createContext<ScrollableHeaderContextType | null>(null);

export function ScrollableHeaderProvider({ children }: PropsWithChildren) {
  const scrollThreshold = useRef(0);
  const lastScrollY = useRef(0);
  const [classNameSuffix, setClassNameSuffix] = useState('');
  const [animationClass, setAnimationClass] = useState('');
  const [headerRoom, setHeaderRoom] = useState(0);
  const [headerTop, setHeaderTop] = useState(0);

  const addHeaderHeight = (height: number) => {
    scrollThreshold.current += height;
  };

  const resetThreshold = () => {
    scrollThreshold.current = 0;
  };

  const handleScroll = () => {
    const currentScroll = document.documentElement.scrollTop || document.body.scrollTop;

    if (currentScroll === 0) {
      setClassNameSuffix('header-unfixed');
      setAnimationClass('header-disable-animation');
    }
    if (currentScroll < lastScrollY.current && currentScroll !== 0) {
      setClassNameSuffix('header-unpinned');
      setAnimationClass('header-scrolled');
    }
    if (currentScroll > scrollThreshold.current && currentScroll > lastScrollY.current && currentScroll !== 0) {
      setClassNameSuffix('header-pinned');
    }

    lastScrollY.current = currentScroll;
  };

  useEffect(() => {
    eventService.fire(WindowEvents.PAGE_SCROLL_EVENT, {
      detail: {
        classNameSuffix,
        animationClass,
        scrollThreshold: scrollThreshold.current,
      },
    });
  }, [classNameSuffix, animationClass, scrollThreshold]);

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', resetThreshold);
    eventService.subscribe(WindowEvents.PAGE_SCROLL_EVENT, handleScroll);

    scrollThreshold.current = 0;

    return () => {
      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', resetThreshold);
      eventService.unsubscribe(WindowEvents.PAGE_SCROLL_EVENT, handleScroll);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    scrollThreshold: scrollThreshold.current,
    addHeaderHeight,
    classNameSuffix,
    animationClass,
    headerRoom,
    setHeaderRoom,
    headerTop,
    setHeaderTop,
  };

  return <ScrollableHeaderContext.Provider value={value}>{children}</ScrollableHeaderContext.Provider>;
}

export function useScrollableHeader() {
  const context = useContext(ScrollableHeaderContext);

  if (!context) throw new Error('useScrollableHeader can only be used in an ScrollableHeaderProvider');

  return context;
}
